import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { ColorSwatch } from "@/components/ColorPicker";
import { Trash2, X, Flag, Repeat } from "lucide-react";
import { Icon } from "@/lib/icons";
import { motion } from "framer-motion";

const PRIORITIES = [
  { v: "high", l: "High", c: "#EF4444" },
  { v: "medium", l: "Medium", c: "#F59E0B" },
  { v: "low", l: "Low", c: "#10B981" },
];
const RECUR = [
  { v: "none", l: "Does not repeat" }, { v: "daily", l: "Every day" },
  { v: "weekly", l: "Every week" }, { v: "monthly", l: "Every month" },
];

export function TaskModal({ open, onClose, initial }) {
  const { categories, tasksApi } = useApp();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (initial) setForm({
      title: "", notes: "", priority: "medium", due_date: null, due_time: null,
      category_id: categories[0]?.id || null, color: null, recurrence: "none", completed: false, ...initial,
    });
  }, [initial, categories]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = !!form.id;

  const save = async () => {
    if (!form.title.trim()) return;
    if (isEdit) await tasksApi.update(form.id, form); else await tasksApi.add(form);
    onClose();
  };
  const del = async () => { if (isEdit) await tasksApi.remove(form.id); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 border-0 gap-0 overflow-hidden" style={{ background: "transparent", boxShadow: "none" }}>
        <DialogTitle className="sr-only">Task</DialogTitle>
        <DialogDescription className="sr-only">Create or edit a task</DialogDescription>
        <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="rounded-3xl overflow-hidden p-5 space-y-4" style={{ background: "var(--bg-sidebar)", border: "1px solid var(--border-subtle)" }} data-testid="task-modal">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--text-primary)" }}>{isEdit ? "Edit Task" : "New Task"}</h3>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: "var(--text-muted)" }}><X size={18} /></button>
          </div>

          <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="What needs to be done?" autoFocus
            className="h-11 rounded-xl" style={{ background: "var(--bg-timeline-track)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} data-testid="task-form-title" />

          <div>
            <label className="text-[11px] uppercase tracking-wider flex items-center gap-1 mb-1.5" style={{ color: "var(--text-muted)" }}><Flag size={12} /> Priority</label>
            <div className="flex gap-2">
              {PRIORITIES.map((p) => (
                <button key={p.v} onClick={() => set("priority", p.v)}
                  className="flex-1 py-2 rounded-xl text-sm font-medium transition-all"
                  style={{ background: form.priority === p.v ? p.c : "var(--bg-timeline-track)", color: form.priority === p.v ? "#fff" : "var(--text-secondary)", border: `1px solid ${form.priority === p.v ? p.c : "var(--border-subtle)"}` }}
                  data-testid={`task-priority-${p.v}`}>{p.l}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Due Date</label>
              <input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value || null)}
                className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)", colorScheme: "dark" }} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Category</label>
              <select value={form.category_id || ""} onChange={(e) => set("category_id", e.target.value || null)}
                className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Custom Color</label>
              <ColorSwatch color={form.color || categories.find((c) => c.id === form.category_id)?.color || "#3B82F6"} onChange={(v) => set("color", v)} testId="task-color-swatch" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider flex items-center gap-1 mb-1.5" style={{ color: "var(--text-muted)" }}><Repeat size={12} /> Repeat</label>
              <select value={form.recurrence} onChange={(e) => set("recurrence", e.target.value)}
                className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}>
                {RECUR.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </div>
          </div>

          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes..." rows={2}
            className="rounded-xl resize-none" style={{ background: "var(--bg-timeline-track)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />

          <div className="flex items-center gap-2 pt-1">
            {isEdit && <Button variant="ghost" onClick={del} className="text-red-400 hover:bg-red-500/10"><Trash2 size={16} /> Delete</Button>}
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" onClick={onClose} style={{ color: "var(--text-secondary)" }}>Cancel</Button>
              <Button onClick={save} className="text-white font-semibold px-6" style={{ background: "var(--accent-primary)" }} data-testid="task-form-submit">{isEdit ? "Save" : "Create"}</Button>
            </div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
